import { useQuery } from "@tanstack/react-query";
import { IconListNumbers } from "@tabler/icons-react";
import { Card, Group, Skeleton, Stack, Table, TablaSkeleton, Tbody, Td, Text, Th, Thead, ThemeIcon, Tr } from "@/components/ui";
import { estaPostEnvio } from "@/lib/estado-solicitud";
import type { SolicitudDetalle } from "@/lib/types";
import { qk } from "@/lib/query-keys";
import { prorrateoApi } from "@/features/prorrateo/services/prorrateo.api";
import { formatearMonto } from "@/shared/format/money";
import { CodigoConNombre } from "@/features/prorrateo/components/CodigoConNombre";

/**
 * Sección read-only del "Prorrateo" (líneas contables) para el resumen del
 * detalle. Consulta el prorrateo confirmado y lo muestra como tabla compacta;
 * si no existe (estado temprano) o no hay acceso, no se renderiza nada. Sin
 * edición — el armado del prorrateo vive en el paso del wizard, exclusivo del
 * dueño. Permite a roles de consulta (Visor / Mesa de Control / Auditor) revisar
 * la dispersión contable sin entrar al wizard.
 */
export function ProrrateoResumenCard({ sol }: { sol: SolicitudDetalle }) {
  const { data, isError, isPending } = useQuery({
    queryKey: qk.prorrateo(sol.id),
    queryFn: () => prorrateoApi.obtener(sol.id),
    staleTime: 30_000,
    retry: false,
  });

  // Primera carga · tarjeta compacta con la altura reservada para no empujar el
  // contenido al llegar. Solo post-envío: enviar a autorización exige el prorrateo
  // confirmado, así que ahí la tarjeta SÍ va a aparecer (antes puede no existir).
  if (isPending) return estaPostEnvio(sol.estado) ? <ProrrateoResumenSkeleton /> : null;

  const lineas = data?.lineas ?? [];
  if (isError || lineas.length === 0) return null;

  const moneda = sol.cfdi?.moneda ?? sol.cfdis[0]?.moneda ?? "MXN";
  const fmt = (n: number) => formatearMonto(n, moneda, { currencyDisplay: "code" });

  return (
    <Card padding="md" id="sec-prorrateo">
      <Group gap="sm" wrap="nowrap">
        <ThemeIcon variant="filled" color="azulProfundo" size="md" radius="md">
          <IconListNumbers size={16} stroke={1.6} />
        </ThemeIcon>
        <Stack gap={0} className="min-w-0">
          <Text fw={600} size="sm">Prorrateo</Text>
          <Text size="xs" c="dimmed">
            {lineas.length} línea{lineas.length === 1 ? "" : "s"} contable{lineas.length === 1 ? "" : "s"}
            {data ? ` · total ${fmt(data.sumaTotal)}` : ""}
          </Text>
        </Stack>
      </Group>

      {/* Scroll vertical acotado · con muchas líneas (dispersión Excel) la tabla
          no crece sin límite. Encabezado sticky para no perder las columnas. */}
      <div className="scrollbar-suave mt-3 max-h-[26rem] overflow-auto">
        <Table verticalSpacing="xs" horizontalSpacing="xs">
          <Thead className="sticky top-0 z-10 bg-card">
            <Tr>
              <Th>#</Th>
              <Th>Descripción</Th>
              <Th>Centro de costo</Th>
              <Th>Cuenta</Th>
              <Th>Indicador</Th>
              <Th align="right">Importe</Th>
            </Tr>
          </Thead>
          <Tbody>
            {lineas.map((l) => (
              <Tr key={l.id}>
                <Td className="whitespace-nowrap tabular-nums">{l.numeroLinea}</Td>
                <Td>
                  <Text size="sm" truncate title={l.descripcion} className="max-w-[220px]">
                    {l.descripcion}
                  </Text>
                </Td>
                <Td>
                  <CodigoConNombre codigo={l.centroDeCostoCodigo} nombre={l.centroDeCostoNombre} mono />
                </Td>
                <Td>
                  <CodigoConNombre codigo={l.cuentaContableCodigo} nombre={l.cuentaContableNombre} mono />
                </Td>
                <Td className="whitespace-nowrap font-mono text-xs">{l.indicador || "—"}</Td>
                <Td align="right" className="whitespace-nowrap tabular-nums">{fmt(l.importe)}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </div>
    </Card>
  );
}

/** Encabezado + 3 filas con las 6 columnas de la tabla compacta del prorrateo. */
function ProrrateoResumenSkeleton() {
  return (
    <Card padding="md" id="sec-prorrateo">
      <div className="flex items-center gap-3" aria-hidden="true">
        <Skeleton height={28} width={28} className="shrink-0" />
        <div className="flex flex-col gap-1.5">
          <Skeleton height={12} width={80} />
          <Skeleton height={10} width={170} />
        </div>
      </div>
      <div className="mt-3">
        <Table verticalSpacing="xs" horizontalSpacing="xs">
          <TablaSkeleton
            dentroDeTabla
            filas={3}
            etiqueta="Cargando prorrateo…"
            columnas={[{ w: 0.5 }, { w: 3 }, { w: 1.5 }, { w: 1.5 }, { w: 1 }, { w: 1.5, align: "right" }]}
          />
        </Table>
      </div>
    </Card>
  );
}
